import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { SiteNav } from "@/components/SiteNav";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/utils/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusOptions = [
  "New",
  "In Review",
  "Proof Sent",
  "In Production",
  "Shipped",
  "Completed",
];

const statusClasses = {
  New: "bg-secondary text-primary",
  "In Review": "bg-amber-200 text-amber-900",
  "Proof Sent": "bg-blue-100 text-blue-800",
  "In Production": "bg-cyan-200 text-cyan-900",
  Shipped: "bg-emerald-200 text-emerald-900",
  Completed: "bg-black text-white",
};

export default function AdminDashboard() {
  const { getAccessTokenSilently } = useAuth();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editStatus, setEditStatus] = useState("New");
  const [adminNotes, setAdminNotes] = useState("");

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: "openid profile email",
        },
      });

      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data || [];
      setOrders(data);

      // Only auto-select the first order if nothing is selected yet.
      setSelectedOrder((prev) => {
        if (prev) return prev;
        if (data.length) {
          const first = data[0];
          setEditStatus(first.status || "New");
          setAdminNotes(first.admin_notes || "");
          return first;
        }
        return prev;
      });
    } catch (error) {
      toast.error("Unable to load orders", {
        description: "Please refresh or check the backend connection.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleSelectOrder = (order) => {
    setSelectedOrder(order);
    setEditStatus(order.status || "New");
    setAdminNotes(order.admin_notes || "");
  };

  const handleUpdate = async () => {
    if (!selectedOrder) return;
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: "openid profile email",
        },
      });

      const response = await axios.put(
        `${API}/orders/${selectedOrder.id}`,
        {
          status: editStatus,
          admin_notes: adminNotes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Order updated", {
        description: `Status set to ${response.data.status}`,
      });

      setSelectedOrder(response.data);
      fetchOrders();
    } catch (error) {
      toast.error("Unable to update order", {
        description: "Try again after refreshing the order list.",
      });
    }
  };

  return (
    <div className="relative" data-testid="admin-page">
      <div className="grain-overlay" data-testid="admin-grain-overlay" />
      <SiteNav mode="app" />

      <main className="px-6 py-12">
        <div className="mx-auto w-full max-w-6xl space-y-10">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p
                className="text-xs font-semibold uppercase tracking-[0.3em]"
                data-testid="admin-kicker"
              >
                Order administration
              </p>
              <h1 className="text-4xl font-bold uppercase" data-testid="admin-title">
                Production queue
              </h1>
            </div>
            <Button
              type="button"
              className="h-10 rounded-none border-2 border-black bg-white px-4 text-xs font-bold uppercase tracking-[0.3em] text-primary transition-colors duration-200 hover:bg-secondary"
              onClick={fetchOrders}
              data-testid="refresh-orders-button"
            >
              Refresh list
            </Button>
          </div>

          <div className="grid gap-8 lg:grid-cols-[1.3fr_1fr]">
            <Card className="rounded-none border-2 border-black">
              <CardHeader>
                <CardTitle
                  className="text-xl font-semibold uppercase"
                  data-testid="orders-table-title"
                >
                  Active orders
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <p className="text-sm text-muted-foreground" data-testid="orders-loading">
                    Loading orders...
                  </p>
                ) : orders.length ? (
                  <Table data-testid="orders-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead data-testid="orders-header-id">Order</TableHead>
                        <TableHead data-testid="orders-header-company">Company</TableHead>
                        <TableHead data-testid="orders-header-qty">Qty</TableHead>
                        <TableHead data-testid="orders-header-status">Status</TableHead>
                        <TableHead data-testid="orders-header-action">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map((order) => (
                        <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                          <TableCell data-testid={`order-id-${order.id}`}>
                            {order.id.slice(0, 8).toUpperCase()}
                          </TableCell>
                          <TableCell data-testid={`order-company-${order.id}`}>
                            {order.company || "—"}
                          </TableCell>
                          <TableCell data-testid={`order-qty-${order.id}`}>
                            {order.quantity}
                          </TableCell>
                          <TableCell data-testid={`order-status-${order.id}`}>
                            <Badge
                              className={`rounded-none ${
                                statusClasses[order.status] || "bg-secondary"
                              }`}
                              data-testid={`order-status-badge-${order.id}`}
                            >
                              {order.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              className="h-8 rounded-none border-2 border-black bg-white px-3 text-[10px] font-bold uppercase tracking-[0.3em] text-primary hover:bg-secondary"
                              onClick={() => handleSelectOrder(order)}
                              data-testid={`order-view-${order.id}`}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground" data-testid="orders-empty">
                    No orders yet. Orders submitted through the builder will appear here.
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="rounded-none border-2 border-black">
              <CardHeader>
                <CardTitle
                  className="text-xl font-semibold uppercase"
                  data-testid="order-detail-title"
                >
                  Order detail
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedOrder ? (
                  <>
                    <div className="space-y-2">
                      <p
                        className="text-xs uppercase tracking-[0.3em] text-muted-foreground"
                        data-testid="detail-id-label"
                      >
                        Order ID
                      </p>
                      <p className="text-lg font-semibold" data-testid="detail-id">
                        {selectedOrder.id.slice(0, 8).toUpperCase()}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <p
                        className="text-xs uppercase tracking-[0.3em] text-muted-foreground"
                        data-testid="detail-specs-label"
                      >
                        Specs
                      </p>
                      <p className="text-sm" data-testid="detail-specs">
                        {selectedOrder.box_style} · {selectedOrder.material} ·{" "}
                        {selectedOrder.length} x {selectedOrder.width} x{" "}
                        {selectedOrder.height} in
                      </p>
                    </div>

                    <div className="space-y-2">
                      <p
                        className="text-xs uppercase tracking-[0.3em] text-muted-foreground"
                        data-testid="detail-contact-label"
                      >
                        Contact
                      </p>
                      <p className="text-sm" data-testid="detail-contact">
                        {selectedOrder.customer_name} · {selectedOrder.email}
                      </p>
                    </div>

                    {selectedOrder.artwork_data_url ? (
                      <div className="space-y-2">
                        <p
                          className="text-xs uppercase tracking-[0.3em] text-muted-foreground"
                          data-testid="detail-artwork-label"
                        >
                          Artwork preview
                        </p>
                        <img
                          src={selectedOrder.artwork_data_url}
                          alt="Order artwork"
                          className="h-32 w-full rounded-none border-2 border-black object-contain"
                          data-testid="detail-artwork-image"
                        />
                      </div>
                    ) : null}

                    <div className="space-y-2">
                      <p
                        className="text-xs uppercase tracking-[0.3em] text-muted-foreground"
                        data-testid="detail-status-label"
                      >
                        Status
                      </p>
                      <Select value={editStatus} onValueChange={setEditStatus}>
                        <SelectTrigger data-testid="detail-status-select">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent data-testid="detail-status-options">
                          {statusOptions.map((status) => (
                            <SelectItem
                              key={status}
                              value={status}
                              data-testid={`detail-status-${status}`}
                            >
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <p
                        className="text-xs uppercase tracking-[0.3em] text-muted-foreground"
                        data-testid="detail-notes-label"
                      >
                        Admin notes
                      </p>
                      <Textarea
                        value={adminNotes}
                        onChange={(event) => setAdminNotes(event.target.value)}
                        rows={4}
                        data-testid="detail-notes-input"
                      />
                    </div>

                    <Button
                      type="button"
                      className="h-11 w-full rounded-none border-2 border-black bg-primary text-xs font-bold uppercase tracking-[0.3em] text-primary-foreground shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
                      onClick={handleUpdate}
                      data-testid="detail-update-button"
                    >
                      Update order
                    </Button>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground" data-testid="detail-empty">
                    Select an order to view details.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
